import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useId } from 'react';

interface FormTextareaProps {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: () => void;
  name?: string;
  autoComplete?: string;
  rows?: number;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  /**
   * Overrides the default `${label}${required ? ' *' : ''}` placeholder
   * derivation (e.g. for CMS-configured placeholders). Existing callers that
   * never pass this keep the exact previous behavior.
   */
  placeholder?: string;
}

const baseClass =
  // Sizing matches FormInput's so textareas read like the neighbouring inputs.
  // That includes the `text-base` below `md`: Mobile Safari zooms the viewport
  // when a focused control's font-size is under 16px, and never zooms back
  // (SH-480).
  'block w-full resize-none rounded-3xl border bg-[#eae8e7] px-5 py-3 text-base md:text-sm text-foreground outline-none placeholder:text-foreground/60 shadow-[inset_0_1px_1px_rgba(0,0,0,0.075)] transition-[border-color,box-shadow] duration-100 focus:border-[rgba(40,25,10,0.2)] focus:shadow-[inset_0_1px_1px_rgba(0,0,0,0.075),0_0_8px_rgba(40,25,10,0.4)] disabled:cursor-not-allowed disabled:opacity-50';

export const FormTextarea = ({
  label,
  value,
  onChange,
  onBlur,
  name,
  autoComplete,
  rows = 5,
  required,
  error,
  disabled,
  placeholder,
}: FormTextareaProps) => {
  const id = useId();
  const errorId = `${id}-error`;
  return (
    <div>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <textarea
        id={id}
        name={name}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        rows={rows}
        disabled={disabled}
        required={required}
        // Derived fallback skips the star when the label already ends with
        // one (CMS-managed labels may carry their own " *").
        placeholder={
          placeholder ??
          `${label}${required && !label.trimEnd().endsWith('*') ? ' *' : ''}`
        }
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        className={classNames(
          baseClass,
          error ? 'border-red-400' : 'border-[#eae8e7]',
        )}
      />
      {error && (
        <p id={errorId} className="mt-1 pl-4 text-xs text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};
